import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeftRight, CheckCircle2, Paperclip, Loader2 } from "lucide-react";
import Reveal from "../components/Reveal";
import { Eyebrow } from "../components/Flow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = ["Residential", "Hotel", "Resort", "Villa", "Commercial", "Mixed-use", "Construction", "Renovation", "Distribution", "Other"];
const STAGES = ["Concept", "Design", "Budgeting", "Tender", "Construction", "Procurement", "Renovation"];
const PRODUCT_OPTIONS = ["Construction", "Finishes", "Bathroom", "Openings & Architectural", "Kitchen", "Lighting", "Furniture", "Exterior"];

const inputCls =
  "w-full rounded-md border border-[#0B132B]/15 bg-white px-4 py-3 text-sm text-[#0B132B] outline-none transition focus:border-[#0D9488] focus:ring-2 focus:ring-[#0D9488]/20 placeholder:text-[#0B132B]/35";
const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B132B]/55";

const Field = ({ label, required, children, testid }) => (
  <div data-testid={testid}>
    <label className={labelCls}>
      {label} {required && <span className="text-[#0D9488]">*</span>}
    </label>
    {children}
  </div>
);

const EMPTY = {
  first_name: "", last_name: "", company: "", job_title: "", email: "", phone: "",
  country: "", city: "", project_type: "", project_name: "", units: "",
  project_size: "", budget: "", project_stage: "", start_date: "", message: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [products, setProducts] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleProduct = (p) =>
    setProducts((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please complete your first name, last name and email.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, products: products.join(", ") });
      if (res.data?.ok) {
        setSent(true);
        toast.success("Project request received.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(res.data?.message || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Could not send your request. Please email us directly at contactbuycaribbean@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pb-24 md:pb-0">
      <section className="bg-[#0B132B] py-20 text-white sm:py-24 grain-overlay relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Eyebrow dark>Contact</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" data-testid="contact-headline">
              LET'S BUILD YOUR PROCUREMENT STRATEGY
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-white/65 sm:text-base">
              Tell us about your project and let us identify where Colombian sourcing can create value.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-[1.6fr_1fr] lg:px-8">
          <Reveal>
            {sent ? (
              <div className="border border-[#0D9488]/40 bg-white p-10 text-center sm:p-14" data-testid="contact-success-panel">
                <CheckCircle2 className="mx-auto h-12 w-12 text-[#0D9488]" strokeWidth={1.4} />
                <h2 className="mt-6 font-display text-xl font-bold sm:text-2xl">Thank you.</h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-[#0B132B]/70">
                  Your project request has been received. BUY CARIBBEAN will review your requirements and contact you.
                </p>
                <p className="mt-6 text-sm text-[#0B132B]/60">
                  Need to add documents?{" "}
                  <a href="mailto:contactbuycaribbean@gmail.com" className="font-bold text-[#0D9488]">
                    contactbuycaribbean@gmail.com
                  </a>
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-[#0B132B]/12 bg-white p-7 sm:p-10" data-testid="contact-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="First name" required testid="field-first-name">
                    <input className={inputCls} data-testid="input-first-name" value={form.first_name} onChange={set("first_name")} placeholder="Maria" />
                  </Field>
                  <Field label="Last name" required testid="field-last-name">
                    <input className={inputCls} data-testid="input-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Restrepo" />
                  </Field>
                  <Field label="Company" testid="field-company">
                    <input className={inputCls} data-testid="input-company" value={form.company} onChange={set("company")} placeholder="Caribbean Development Group" />
                  </Field>
                  <Field label="Job title" testid="field-job-title">
                    <input className={inputCls} data-testid="input-job-title" value={form.job_title} onChange={set("job_title")} placeholder="Development Director" />
                  </Field>
                  <Field label="Email" required testid="field-email">
                    <input type="email" className={inputCls} data-testid="input-email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </Field>
                  <Field label="Phone" testid="field-phone">
                    <input className={inputCls} data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+1 809 000 0000" />
                  </Field>
                  <Field label="Country" testid="field-country">
                    <input className={inputCls} data-testid="input-country" value={form.country} onChange={set("country")} placeholder="Dominican Republic" />
                  </Field>
                  <Field label="City" testid="field-city">
                    <input className={inputCls} data-testid="input-city" value={form.city} onChange={set("city")} placeholder="Punta Cana" />
                  </Field>
                  <Field label="Project type" testid="field-project-type">
                    <select className={inputCls} data-testid="select-project-type" value={form.project_type} onChange={set("project_type")}>
                      <option value="">Select project type</option>
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Project stage" testid="field-project-stage">
                    <select className={inputCls} data-testid="select-project-stage" value={form.project_stage} onChange={set("project_stage")}>
                      <option value="">Select project stage</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Project name" testid="field-project-name">
                    <input className={inputCls} data-testid="input-project-name" value={form.project_name} onChange={set("project_name")} placeholder="Caribbean Residence" />
                  </Field>
                  <Field label="Number of units / rooms" testid="field-units">
                    <input className={inputCls} data-testid="input-units" value={form.units} onChange={set("units")} placeholder="120" />
                  </Field>
                  <Field label="Estimated project size" testid="field-project-size">
                    <input className={inputCls} data-testid="input-project-size" value={form.project_size} onChange={set("project_size")} placeholder="e.g. 18,000 m²" />
                  </Field>
                  <Field label="Estimated procurement budget" testid="field-budget">
                    <input className={inputCls} data-testid="input-budget" value={form.budget} onChange={set("budget")} placeholder="e.g. $1M – $5M" />
                  </Field>
                  <Field label="Expected start date" testid="field-start-date">
                    <input type="date" className={inputCls} data-testid="input-start-date" value={form.start_date} onChange={set("start_date")} />
                  </Field>
                  <Field label="Upload project documents" testid="field-documents">
                    <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-[#0B132B]/25 bg-[#F8F7F4] px-4 py-3 text-sm text-[#0B132B]/55 transition hover:border-[#0D9488]" data-testid="input-documents">
                      <Paperclip className="h-4 w-4 text-[#0D9488]" />
                      Optional — large files can be emailed to us
                      <input type="file" className="hidden" disabled />
                    </label>
                  </Field>
                </div>

                <div className="mt-6" data-testid="field-products">
                  <label className={labelCls}>Products required</label>
                  <div className="flex flex-wrap gap-2">
                    {PRODUCT_OPTIONS.map((p) => (
                      <button
                        type="button"
                        key={p}
                        onClick={() => toggleProduct(p)}
                        data-testid={`product-chip-${p.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                          products.includes(p)
                            ? "border-[#0D9488] bg-[#0D9488] text-white"
                            : "border-[#0B132B]/20 bg-white text-[#0B132B]/70 hover:border-[#0D9488]"
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <Field label="Message" testid="field-message">
                    <textarea rows={5} className={inputCls} data-testid="input-message" value={form.message} onChange={set("message")} placeholder="Tell us about your project, timeline and what you need to source." />
                  </Field>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-form-submit-button"
                  className="btn-sweep btn-sweep-navy mt-8 inline-flex w-full items-center justify-center gap-2 bg-[#0D9488] px-9 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white disabled:opacity-60 sm:w-auto"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  REQUEST A PROJECT REVIEW
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={150}>
            <aside className="space-y-6 lg:sticky lg:top-24">
              <div className="border border-[#0B132B]/12 bg-[#0B132B] p-8 text-white" data-testid="contact-info-card">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#14B8A6]">Direct contact</p>
                <a href="mailto:contactbuycaribbean@gmail.com" data-testid="contact-email-link" className="mt-4 flex items-start gap-3 text-sm font-semibold text-white/85 transition-colors hover:text-white">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#14B8A6]" />
                  contactbuycaribbean@gmail.com
                </a>
                <p className="mt-5 flex items-center gap-3 text-sm text-white/70">
                  <ArrowLeftRight className="h-4 w-4 text-[#14B8A6]" />
                  Colombia ↔ Caribbean
                </p>
              </div>
              <div className="border border-[#0B132B]/12 bg-white p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B132B]/45">What happens next</p>
                <ol className="mt-5 space-y-4">
                  {["We review your project requirements.", "We identify where Colombian sourcing creates value.", "We contact you with a first procurement perspective."].map((s, i) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-[#0B132B]/75">
                      <span className="font-display text-sm font-extrabold text-[#0D9488]">{i + 1}.</span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="border border-[#0B132B]/12 bg-white p-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B132B]/45">Plans or BOQ ready?</p>
                <p className="mt-3 text-sm leading-relaxed text-[#0B132B]/70">
                  Send plans, specifications or a bill of quantities directly by email — we'll take it from there.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
